import React, {useEffect, useRef, useState} from 'react';
import { classNames } from '../../Services/helpers';
import Icon from '../Icons/Icon';
import { useAssistantContext } from './Provider';
import Markdown from '../Formatting/Markdown';
import Loading from '../Loading';
import ErrorBoundary from '../Containers/ErrorBoundary/ErrorBoundary';
import Animation from '../Animation';
import Dropdown from '../Dropdown';

const Content = () => {

    return <div className="h-full flex flex-col justify-between">
        <ErrorBoundary>
            <Messages />
            <ReplyBox />
        </ErrorBoundary>
    </div>
}

const ReplyBox = () => {
    const {dictationAvailable, tmpQuestion, setTmpQuestion, dictating, toggleDictation, submitMessage, isLoading, newConversation, requestHumanAttention} = useAssistantContext();

    const inputRef = useRef(null);

    useEffect(() => {
        if (!inputRef?.current) {
            return;
        }
        inputRef.current.style.height = '1px';
        inputRef.current.style.height = `max(1.5rem, ${inputRef.current.scrollHeight}px)`;
    }, [tmpQuestion])

    const submit = (e) => {
        if (e) {
            e.preventDefault();
            e.stopPropagation();
        }
        if (!tmpQuestion) {
            return;
        }
        submitMessage(tmpQuestion);
        setTmpQuestion('');
    }
    return <div className="w-full flex flex-row items-center">
            <Dropdown
                options={[
                    {
                        title: 'New conversation',
                        icon: 'RotateRight',
                        onClick: () => newConversation(),
                    },
                    {
                        title: 'Human attention',
                        icon: 'Hand',
                        onClick: () => requestHumanAttention(),
                    },
                ]}
                openUp
                openRight
                flush
            />

            <div className={classNames(
                'grow bg-white',
                'm-2 ml-0 flex justify-between items-top border border-slate-200 rounded-lg',
                dictating ? 'ring-2 ring-red-600 fill-red-600' : 'focus-within:ring-2 ring-brand',
            )}>

            {dictationAvailable && <div className={classNames(
                    'w-8 h-8 shrink-0 grow-0 rounded-full mr-1 flex justify-center items-center',
                    'hover:fill-red-600 cursor-pointer',
                    dictating ? 'fill-red-600' : 'fill-muted',
                )} onClick={toggleDictation}>
                    <Icon icon="Microphone" theme={dictating ? 'solid' : 'light'} />
            </div>}

            <form onSubmit={submit} className="contents">
                <textarea
                    ref={inputRef}
                    placeholder="Ask me anything!"
                    value={tmpQuestion}
                    onChange={e => setTmpQuestion(e.target.value)}
                    onKeyUp={e => e.key === 'Enter' && submit(e)}
                    className="flex-grow p-1 border-none focus:outline-none focus:ring-0 text-sm text-muted rounded-lg text-wrap overflow-none resize-none disabled:bg-slate-100 disabled:text-gray-500"
                />
            </form>
            {isLoading && <Loading className="m-2" />}
            {!isLoading && <Icon theme="solid"
                icon="CircleArrowRight"
                className={classNames('m-2', tmpQuestion ? 'fill-brand' : 'fill-muted')}
                onClick={submit}
            />}
        </div>
    </div>
}

const Messages = () => {
    const {conversation, entries, assistantTyping, streamingAnswers, activeToolTitles, sendingQuestion, assistantOpen, submitMessage} = useAssistantContext();
    const scrollRef = useRef(null);
    const [userHasScrolled, setUserHasScrolled] = useState(false);
    const [shouldAutoScroll, setShouldAutoScroll] = useState(true);

    const scrollToBottom = () => {
        if (!scrollRef.current || !shouldAutoScroll) {
            return;
        }
        scrollRef.current.scrollTo({
            top: scrollRef.current.scrollHeight,
            behavior: 'instant',
        });
    };

    const handleScroll = () => {
        if (!scrollRef.current) return;

        const { scrollTop, scrollHeight, clientHeight } = scrollRef.current;
        const isAtBottom = Math.abs(scrollHeight - clientHeight - scrollTop) < 10;

        setUserHasScrolled(true);
        setShouldAutoScroll(isAtBottom);
    };

    useEffect(() => {
        if (!userHasScrolled) {
            scrollToBottom();
        } else if (shouldAutoScroll) {
            scrollToBottom();
        }

        // Delayed scroll for content that might take time to render
        setTimeout(() => {
            if (!userHasScrolled || shouldAutoScroll) {
                scrollToBottom();
            }
        }, 100);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [entries, streamingAnswers, sendingQuestion, assistantOpen, activeToolTitles, userHasScrolled, shouldAutoScroll]);

    const theEntries = [...entries, {role: 'User', message: sendingQuestion, sending: true}];

    return <div
        ref={scrollRef}
        onScroll={handleScroll}
        className={classNames(
            'grow m-1 mt-0 px-1 pt-1 rounded-lg overflow-y-scroll no-scroll-bar flex flex-col gap-2',
            'text-sm text-wrap prose prose-sm',
        )}
    >
        {theEntries.map((message, index) => {
            if (message.type === 'assistantActivated' || message.type === 'assistantDeactivated') {
                return <SystemMessage key={index}
                    message={
                        message.type === 'assistantActivated' ?
                            'Sid has joined the conversation' :
                            'Sid has left the conversation'
                        }
                />
            }

            if (message.type === 'humanAttentionRequested' || message.type === 'humanAttentionRemoved') {
                return <SystemMessage key={index}
                    message={
                        message.type === 'humanAttentionRequested' ?
                            'The conversation has been flagged for human attention' :
                            null
                        }
                />
            }

            if (!message.message) {
                return null;
            }
            if (message.role === 'System' || message.message.startsWith('#INTERNAL')) {
                return null;
            }
            if (message.sending) {
                return <p key={index} className={classNames(
                    'max-w-[90%] p-2 rounded-lg',
                    'border border-dashed border-blue-100',
                    'self-end bg-slate-50 text-muted italic prose-gray',
                )}>
                    {message.message}
                </p>
            }

            return <div key={index} className={classNames(
                    'max-w-[90%] p-2 rounded-lg',
                    message.role === 'User' ? 'self-end text-right italic prose-gray' : 'self-start left-left prose-slate',
                    message.role === 'User' ? 'bg-sky-100 rounded-br-none' : 'bg-slate-100 rounded-bl-none',
                    message.role === 'Admin' && 'border border-brand',
                )}>
                <Markdown markdown={message.message}
                    className="[&>:first-child]:mt-0 [&>:last-child]:mb-0"
                    btnCallback={submitMessage}
                />
                {message.role === 'Admin' && <p className="mt-2 mb-0 text-muted text-xs">Malcolm</p>}
            </div>
        })}

        {assistantTyping && <div className={classNames(
                'max-w-[90%] px-2 rounded-lg rounded-bl-none',
                'self-start left-left prose-slate',
                'bg-gray-100',
            )}>
            <Animation name="typing" />
        </div>}

        {streamingAnswers && streamingAnswers.map((streamingAnswer, index) => {
            if (!streamingAnswer || !streamingAnswer.trim()) {
                return null;
            }
            return <div key={index} className={classNames(
                    'max-w-[90%] p-2 rounded-lg',
                    'self-start left-left prose-slate',
                    'bg-gray-100',
                )}>
                <Markdown markdown={streamingAnswer} className="[&>:first-child]:mt-0 [&>:last-child]:mb-0" />
            </div>
        })}

        {activeToolTitles && activeToolTitles.map(activeToolTitle => {
            return <div className={classNames(
                    'flex items-center max-w-[90%]',
                    'py-2 px-4 rounded-lg',
                    'self-start left-left prose-slate',
                    'bg-orange-100',
                )}>
                <Loading className="mr-2" />
                {activeToolTitle}
            </div>
        })}

        {conversation?.assistantError && <div className="max-w-[90%] mx-auto text-center text-balance p-2 rounded-lg bg-red-50/50 text-red-600">
            {conversation.assistantError}
        </div>}

        <Status />
    </div>
}

const Status = () => {
    const {conversation} = useAssistantContext();

    if (!conversation) {
        return null;
    }

    if (!conversation.assistantActive) {
        return <p className="mt-1 text-muted text-xs text-center">
            The bot has left, you're speaking to a human.
        </p>
    }

    return <p className="mt-1 text-muted text-xs text-center">
        Sid is still learning and might make mistakes.<br />
        {conversation.humanAttention ?
            'A human will join the conversation shortly.' :
            'You can ask to speak to a human at any time.'}
    </p>
}

const SystemMessage = ({message}) => {
    if (!message) {
        return null;
    }

    return <p className={classNames(
        'my-0 max-w-[80%] p-2 rounded-lg',
        // 'border border-dashed border-blue-100',
        'self-center bg-slate-50 text-muted italic prose-gray text-center text-balance',
    )}>
        {message}
    </p>
}

export default Content;
